import { cn } from "@devhop/ui";
import { useEffect, useRef, useState } from "react";

type FlashDirection = "up" | "down" | "neutral";

export function parseComparableMonitorNumber(raw: string): number | null {
  const cleaned = raw.replace(/[^\d.-]/g, "");
  if (!cleaned || cleaned === "-" || cleaned === ".") return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

/**
 * ตัวเลขบนจอ monitor — เมื่อค่าเปลี่ยนจะ flash สี + scale และโชว์ delta สั้นๆ
 * (ขึ้น = เขียว, ลง = แดง). เคารพ prefers-reduced-motion ผ่าน motion-safe.
 */
export function MonitorAnimatedValue({
  value,
  enabled = false,
  className,
  unitClassName,
  unit,
}: {
  value: string;
  enabled?: boolean;
  className?: string;
  unitClassName?: string;
  unit?: string;
}) {
  const previousRef = useRef<string | null>(null);
  const [flash, setFlash] = useState<FlashDirection | null>(null);
  const [delta, setDelta] = useState<number | null>(null);

  useEffect(() => {
    if (!enabled) {
      previousRef.current = value;
      setFlash(null);
      setDelta(null);
      return;
    }

    const previous = previousRef.current;
    previousRef.current = value;
    if (previous == null || previous === value) return;

    const prevNum = parseComparableMonitorNumber(previous);
    const nextNum = parseComparableMonitorNumber(value);
    let direction: FlashDirection = "neutral";
    let nextDelta: number | null = null;
    if (prevNum != null && nextNum != null && prevNum !== nextNum) {
      direction = nextNum > prevNum ? "up" : "down";
      nextDelta = nextNum - prevNum;
    }

    setFlash(direction);
    setDelta(nextDelta);
    const timer = window.setTimeout(() => {
      setFlash(null);
      setDelta(null);
    }, 1100);
    return () => window.clearTimeout(timer);
  }, [enabled, value]);

  return (
    <span
      className={cn(
        "relative inline-flex items-baseline gap-1.5 tabular-nums",
        className,
      )}
    >
      <span
        className={cn(
          "inline-block origin-center transition-[color,transform] duration-300 ease-out",
          flash != null &&
            "motion-safe:scale-110 motion-safe:animate-[monitor-value-pulse_0.85s_ease-out]",
          flash === "up" && "text-emerald-600 dark:text-emerald-400",
          flash === "down" && "text-rose-600 dark:text-rose-400",
          flash === "neutral" && "text-sky-600 dark:text-sky-400",
        )}
      >
        {value}
      </span>
      {unit ? <span className={unitClassName}>{unit}</span> : null}
      {enabled && flash != null && delta != null ? (
        <span
          className={cn(
            "font-semibold motion-safe:animate-[monitor-delta-fade_1.1s_ease-out_forwards]",
            "text-xl",
            flash === "up" && "text-emerald-600 dark:text-emerald-400",
            flash === "down" && "text-rose-600 dark:text-rose-400",
          )}
          aria-hidden
        >
          {delta > 0 ? `+${delta}` : String(delta)}
        </span>
      ) : null}
    </span>
  );
}
